import { useMemo } from 'react';
import type { CSSProperties, ReactElement } from 'react';
import { useNavigate } from 'react-router';

import { LanguageRoute } from '@/config/router/routes';
import type { L10n } from '@/l10n/types';
import { useL10n } from '@/l10n/useL10n';
import { useChatController } from '@/presentation/providers/chatController/chatControllerContext';
import type { ChatValue } from '@/presentation/providers/chatController/chatValue';
import { AnimatedSwitcher } from '@/presentation/widgets/AnimatedSwitcher';
import { IgnoreAnimatedSwitcher } from '@/presentation/widgets/IgnoreAnimatedSwitcher';
import { MicButton } from '@/presentation/widgets/MicButton';
import type { MicButtonSize } from '@/presentation/widgets/MicButton';
import { MicButtonPlaceholder } from '@/presentation/widgets/MicButtonPlaceholder';
import { PrimaryIconButton } from '@/presentation/widgets/PrimaryIconButton';
import { wv } from '@/utils/screenUtil';
import { useScreen } from '@/utils/useScreen';

import { CHAT_ACTION_TYPE_SPEC, ChatActionType } from './chatActionType';
import styles from './ChatActions.module.css';

/** Перенос `chat_page/chat_actions.dart` — микрофон и кнопки под ним. */

/** `Size(200.w, 200.w)` */
const MIC_BUTTON_SIZE = 200;

/** `AnimatedSwitcher(duration: Duration(milliseconds: 400))` у подписи. */
const DESCRIPTION_SWITCH_DURATION_MS = 400;

/** `final ignore = switch (type) { … }` */
function isIgnoring(type: ChatActionType, personAnimating: boolean): boolean {
  switch (type) {
    case ChatActionType.backButton:
    case ChatActionType.closeConnection:
      return false;
    case ChatActionType.keyboardType:
    case ChatActionType.selectLanguage:
      return personAnimating;
  }
}

interface ActionSlotProps {
  readonly action: ChatActionType | null;
  /** `ValueKey('empty')` слева и `ValueKey('none')` справа — как в Dart. */
  readonly emptyKey: string;
  readonly personAnimating: boolean;
  readonly onPressed: (type: ChatActionType) => void;
}

/** `SizedBox(width: size, height: size, child: Center(IgnoreAnimatedSwitcher))` */
function ActionSlot(props: ActionSlotProps): ReactElement {
  const { action, emptyKey, personAnimating, onPressed } = props;
  return (
    <div className={styles.slot}>
      <IgnoreAnimatedSwitcher switchKey={action ?? emptyKey}>
        {action === null ? null : (
          <ActionButton
            action={action}
            personAnimating={personAnimating}
            onPressed={onPressed}
          />
        )}
      </IgnoreAnimatedSwitcher>
    </div>
  );
}

interface ActionButtonProps {
  readonly action: ChatActionType;
  readonly personAnimating: boolean;
  readonly onPressed: (type: ChatActionType) => void;
}

/** `buildAction({required ChatActionType type})` из Dart. */
function ActionButton({ action, personAnimating, onPressed }: ActionButtonProps): ReactElement {
  const ignore = isIgnoring(action, personAnimating);
  const spec = CHAT_ACTION_TYPE_SPEC[action];
  return (
    <div className={ignore ? styles.ignoring : undefined}>
      <PrimaryIconButton
        size={spec.buttonSize}
        iconSize={spec.iconSize}
        backgroundColor={spec.backgroundColor}
        icon={spec.icon}
        onPressed={ignore ? null : () => onPressed(action)}
      />
    </div>
  );
}

interface MicSlotProps {
  readonly chatValue: ChatValue;
  readonly voiceEnabled: boolean;
  readonly size: MicButtonSize;
  readonly onHoldChange: (isHolding: boolean) => void;
}

function MicSlot(props: MicSlotProps): ReactElement {
  const { chatValue, voiceEnabled, size, onHoldChange } = props;
  if (chatValue.personAnimating) return <MicButtonPlaceholder size={size} />;
  if (!voiceEnabled) {
    // STT не настроен: кнопка видна, но не нажимается —
    // причина написана в подписи под ней.
    return (
      <div className={styles.micDisabled}>
        <MicButton
          size={size}
          isSpeeking={false}
          isLoading={false}
          isRecording={false}
          onStart={() => {}}
          onEnd={() => {}}
        />
      </div>
    );
  }
  return (
    <MicButton
      size={size}
      isSpeeking={chatValue.speeking}
      isLoading={chatValue.translating || chatValue.fetchingResponse}
      isRecording={chatValue.isRecording}
      onStart={() => onHoldChange(true)}
      onEnd={() => onHoldChange(false)}
    />
  );
}

export interface ChatActionsProps {
  readonly chatValue: ChatValue;
  readonly chatMode: boolean;
  readonly description?: string | null;
  readonly leftAction?: ChatActionType | null;
  readonly rightAction?: ChatActionType | null;
  /**
   * У аватара не настроено распознавание речи — микрофон выключен
   * превентивно, остаётся клавиатурный режим.
   */
  readonly voiceInputEnabled?: boolean;
}

/** Подпись под микрофоном — лестница условий из Dart, один в один. */
function describeState(
  props: ChatActionsProps,
  voiceEnabled: boolean,
  l10n: L10n,
): string {
  const { chatValue, chatMode, description } = props;
  if (chatMode) return '';
  if (description != null) return description;
  if (chatValue.personAnimating) return l10n.wait;
  if (!voiceEnabled) return l10n.voice_input_unavailable;
  if (!chatValue.busy) return l10n.press_to_speek;
  return '';
}

export function ChatActions(props: ChatActionsProps): ReactElement {
  const { chatValue, leftAction = null, rightAction = null } = props;
  const voiceEnabled = props.voiceInputEnabled ?? true;
  const chatController = useChatController();
  const navigate = useNavigate();
  const l10n = useL10n();
  const { sw } = useScreen();

  const micButtonSize = useMemo(
    () => ({ width: MIC_BUTTON_SIZE * sw, height: MIC_BUTTON_SIZE * sw }),
    [sw],
  );

  /** `double get size` — коробка под большую из двух кнопок. */
  const slotSize = Math.max(
    leftAction === null ? 0 : CHAT_ACTION_TYPE_SPEC[leftAction].buttonSize,
    rightAction === null ? 0 : CHAT_ACTION_TYPE_SPEC[rightAction].buttonSize,
  );

  const description = describeState(props, voiceEnabled, l10n);

  const handlePressed = (type: ChatActionType): void => {
    switch (type) {
      case ChatActionType.backButton:
        // `context.pop()`. В go_router `go()` пересобирал стек по дереву
        // маршрутов, и под этим экраном всегда лежал его родитель —
        // `/select-language`. В React Router истории такой формы нет, а
        // `navigate(-1)` увёл бы туда, откуда пришли (после смены входного
        // экрана — к списку аватаров). Поэтому переход явный.
        LanguageRoute.go(navigate);
        break;
      case ChatActionType.closeConnection:
        chatController.closeCurrentConnection();
        break;
      case ChatActionType.keyboardType:
        chatController.enableKeyboardMode();
        break;
      case ChatActionType.selectLanguage:
        // Та же `context.pop()`, что и у стрелки, — отличается только значок.
        LanguageRoute.go(navigate);
        break;
    }
  };

  const ignoring = chatValue.keyboardMode || chatValue.personAnimating;
  const style = {
    '--action-size': wv(slotSize),
    '--actions-opacity': chatValue.keyboardMode ? 0 : 1,
  } as CSSProperties;

  return (
    <div
      className={`${styles.root} ${ignoring ? styles.ignoring : ''}`}
      style={style}
    >
      <div className={styles.row}>
        <ActionSlot
          action={leftAction}
          emptyKey="empty"
          personAnimating={chatValue.personAnimating}
          onPressed={handlePressed}
        />
        <IgnoreAnimatedSwitcher
          switchKey={`${chatValue.personAnimating}|${voiceEnabled}`}
        >
          <MicSlot
            chatValue={chatValue}
            voiceEnabled={voiceEnabled}
            size={micButtonSize}
            onHoldChange={(isHolding) => chatController.changeHolding(isHolding)}
          />
        </IgnoreAnimatedSwitcher>
        <ActionSlot
          action={rightAction}
          emptyKey="none"
          personAnimating={chatValue.personAnimating}
          onPressed={handlePressed}
        />
      </div>
      <div className={styles.descriptionPadding}>
        <AnimatedSwitcher
          switchKey={description}
          duration={DESCRIPTION_SWITCH_DURATION_MS}
          className={styles.descriptionSwitcher}
        >
          <span className={styles.description}>{description}</span>
        </AnimatedSwitcher>
      </div>
      <div className={styles.bottomGap} />
    </div>
  );
}
